import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "React hooks in a nut shell 🥐",
  "description": "",
  "published": "2018-10-29T00:00:00.000Z",
  "redirect_from": ["/blog/react-hooks/swizec/8681"],
  "categories": "Front End, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    {
      /* wp:html */
    }
    <p>{`There's no hook emoji… croissants look like hooks, right?`}</p>
    <p>{`Mmmm… croissants with some sort of nut cream filling. Get it, hooks in a nutshell? Ha`}</p>
    <p>{`Maybe I'm hungry 🤔`}</p>
    <p>{`So anyway, React hooks took the React world by storm this weekend. They're not even out yet! And yet here we are.`}</p>
    <p>{`JavaScript lyfe: New thing comes out, rocks the world, everyone says it's the next best thing since jQuery and will definitely make all your code better and all your knowledge obsolete. Time to learn stuff!`}</p>
    <p>{`Untrue.`}</p>
    <p>{`React hooks are neat. They look useful. They don't deprecate anything. Please don't rewrite your apps in hooks. Yet.`}</p>
    <p>{`I watched `}<a parentName="p" {...{
        "href": "https://www.youtube.com/watch?v=wXLf18DsV-I"
      }}>{`Ryan's talk 90% cleaner with hooks`}</a>{` last night, `}<a parentName="p" {...{
        "href": "https://www.youtube.com/watch?v=V-QO-KO90iQ"
      }}>{`Dan's and Sophie's talk announcing hooks`}</a>{` this morning, read all the docs, and watched Twitter like a disinterested hawk all weekend.`}</p>
    <p>{`Hours of research condensed into the next 200 words because I love you. ❤️`}</p>
    <h2 {...{
      "id": "heres-what-you-need-to-know-about-hooks-right-now-"
    }}>{`Here's what you need to know about hooks right now 👇`}</h2>
    <p>{`You can try them out.`}</p>
    <ol>
      <li parentName="ol"><inlineCode parentName="li">{`create-react-app look-ma-no-classes`}</inlineCode></li>
      <li parentName="ol">{`Open package.json, change `}<inlineCode parentName="li">{`react`}</inlineCode>{` and `}<inlineCode parentName="li">{`react-dom`}</inlineCode>{` to `}<inlineCode parentName="li">{`next`}</inlineCode></li>
      <li parentName="ol"><inlineCode parentName="li">{`yarn install`}</inlineCode></li>
    </ol>
    <p><img parentName="p" {...{
        "src": "/6e8ad489ff7d4005682651dbb0ab99ca/media-5eF7npmWqLwuECXPNT-giphy.gif",
        "alt": "Try out hooks"
      }}></img></p>
    <p>{`Hooks exist to rid your codebase of classes. Because classes are confusing to both people and compilers.`}</p>
    <p>{`Where you used to class, you can now hook. In your functional components.`}</p>
    <p>{`Built-in hooks cover the most common uses cases. You can build your own for everything else.`}</p>
    <h3 {...{
      "id": "usestate"
    }}>{`useState`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`useState`}</inlineCode>{` hook replaces pairs of state getters and setters.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`  class myComponent extends React.Component {
    state = {
      value: 'default'
    }

    handleChange = (e) => this.setState({
      value: e.target.value
    })

    render() {
      const { value } = this.state;

      return <input value={value} onchange={handleChange}>
    }
}
`}</code></pre>
    <p>{`👇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const myComponent = () => {
  const [value, setValue] = useState('default');

  const handleChange = (e) => setValue(e.target.value)

  return <input value={value} onchange={handleChange}>
}
`}</code></pre>
    <p>{`Less code to write and understand.`}</p>
    <p>{`In a class component, you:`}</p>
    <ul>
      <li parentName="ul">{`set a default value`}</li>
      <li parentName="ul">{`create an `}<inlineCode parentName="li">{`onChange`}</inlineCode>{` callback that fires `}<inlineCode parentName="li">{`setState`}</inlineCode></li>
      <li parentName="ul">{`read value from state before rendering etc.`}</li>
    </ul>
    <p>{`Without modern fat arrow syntax, you might run into trouble with binds.`}</p>
    <p>{`The hook approach moves that boilerplate to React's plate. You call `}<inlineCode parentName="p">{`useState`}</inlineCode>{`. It takes a default value and returns a getter and a setter.`}</p>
    <p>{`You call that setter in your change handler.`}</p>
    <p>{`Behind the scenes, React subscribed your component to that change. Your component re-renders.`}</p>
    <h3 {...{
      "id": "useeffect"
    }}>{`useEffect`}</h3>
    <p><inlineCode parentName="p">{`useEffect`}</inlineCode>{` replaces the `}<inlineCode parentName="p">{`componentDidMount`}</inlineCode>{`, `}<inlineCode parentName="p">{`componentDidUpdate`}</inlineCode>{`, `}<inlineCode parentName="p">{`shouldComponentUpdate`}</inlineCode>{`, `}<inlineCode parentName="p">{`componentWillUnmount`}</inlineCode>{` quadfecta. It's like a trifecta, but four.`}</p>
    <p>{`Say you want a side-effect when your component updates, like make an API call. Gotta run it on mount and update. Want to subscribe to a DOM event? Gotta unsubscribe on unmount.`}</p>
    <p>{`Wanna do all this only when certain props change? Gotta check for that.`}</p>
    <p>{`Class:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class myComp extends Component {
  state = {
      value: 'default'
    }

    handleChange = (e) => this.setState({
      value: e.target.value
    })

    saveValue = () => fetch('/my/endpoint', {
        method: 'POST'
        body: this.state.value
    })

    componentDidMount() {
        this.saveValue();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.value !== this.state.value) {
            this.saveValue()
        }
    }

    render() {
      const { value } = this.state;

      return <input value={value} onchange={handleChange}>
    }
}
`}</code></pre>
    <p>{`👇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const myComponent = () => {
  const [value, setValue] = useState('default');

  const handleChange = (e) => setValue(e.target.value)
  const saveValue = () => fetch('/my/endpoint', {
        method: 'POST'
        body: this.state.value
    })

    useEffect(saveValue, [value]);

  return <input value={value} onchange={handleChange}>
}
`}</code></pre>
    <p>{`So much less code!`}</p>
    <p><inlineCode parentName="p">{`useEffect`}</inlineCode>{` runs your function on `}<inlineCode parentName="p">{`componentDidMount`}</inlineCode>{` and `}<inlineCode parentName="p">{`componentDidUpdate`}</inlineCode>{`. And that second argument, the `}<inlineCode parentName="p">{`[value]`}</inlineCode>{` part, tells it to run only when that value changes.`}</p>
    <p>{`No need to double check with a conditional. If your effect updates the component itself through a state setter, the second argument acts as a `}<inlineCode parentName="p">{`shouldComponentUpdate`}</inlineCode>{` of sorts.`}</p>
    <p>{`When you return a method from `}<inlineCode parentName="p">{`useEffect`}</inlineCode>{`, it acts as a `}<inlineCode parentName="p">{`componentWillUnmount`}</inlineCode>{`. Listening to, say, your mouse position looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const [mouseX, setMouseX] = useState();
const handleMouse = (e) => setMouseX(e.screenX);

useEffect(() => {
  window.addEventListener("mousemove", handleMouse);
  return () => window.removeEventListener(handleMouse);
});
`}</code></pre>
    <p>{`Neat 👌`}</p>
    <p>{`Waiting for the day we can use this to do naughty DOM stuff in functional components. Limiting factor right now is that we can't have refs I think.`}</p>
    <h3 {...{
      "id": "usecontext"
    }}>{`useContext`}</h3>
    <p><inlineCode parentName="p">{`useContext`}</inlineCode>{` cleans up your render prop callbacky hell.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`<somecontext>
  {state => ...}
</somecontext>
`}</code></pre>
    <p>{`👇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const state = useContext(SomeContext);
`}</code></pre>
    <p>{`Context becomes just a value in your function. React auto subscribes you to all updates.`}</p>
    <h3 {...{
      "id": "usereducer"
    }}>{`useReducer`}</h3>
    <p><inlineCode parentName="p">{`useReducer`}</inlineCode>{` is like React got Redux built in now. Whoa.`}</p>
    <p>{`You don't really have to know this one. But it comes by default and it's kinda neat. Although I think using it for realz will lead to your components becoming way too big and bloated.`}</p>
    <p>{`You should `}<a parentName="p" {...{
        "href": "https://youtu.be/wXLf18DsV-I?t=1457"
      }}>{`watch Ryan's talk`}</a>{` to learn more about this one. Too long to explain in a nutshell 🥐 post.`}</p>
    <lite-youtube {...{
      "videoid": "wXLf18DsV-I",
      "videostartat": "1538"
    }}></lite-youtube>
    {
      /* /wp:html */
    }
    {
      /* wp:heading */
    }
    <h2 {...{
      "id": "so"
    }}>{`So?`}</h2>
    {
      /* /wp:heading */
    }
    {
      /* wp:paragraph */
    }
    <p>{`Will hooks change the way you write React forever? We'll see. The internet is excited.`}</p>
    {
      /* /wp:paragraph */
    }
    {
      /* wp:paragraph */
    }
    <p>{`You can write your own hooks. Repositories of hooks are popping up like crazy. I'm sure your internet will be full of them for the next while.`}</p>
    {
      /* /wp:paragraph */
    }
    {
      /* wp:paragraph */
    }
    <p>{`Most importantly 👇`}</p>
    {
      /* /wp:paragraph */
    }
    {
      /* wp:paragraph */
    }
    <p>{`You can write your own hooks and re-use functionality between components. It's just a function.`}</p>
    {
      /* /wp:paragraph */
    }
    {
      /* wp:paragraph */
    }
    <p>{`Dan and Sophie say this is the future of React. All functional. No classes.`}<br parentName="p"></br>{`
`}{`But right now, it's just a Request For Comments. So comment away.`}</p>
    {
      /* /wp:paragraph */
    }
    {
      /* wp:paragraph */
    }
    {
      /* /wp:paragraph */
    }

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      